import React, { useState } from 'react';
import {
  Box, Stack, Typography,
} from '@mui/material';
import { AiGeneratedChip, OutlinedIcons } from '@marageti/z4-lib';
import { SignificantDate } from '@marageti/z4-sdk/lib/people';
import { EditSignificantDateModal } from '../modals/edit-significant-date-modal';
import useTravelerProfile from '../../hooks/use-traveler-profile';
import SuggestionSlot from '../traveler-profile-suggestions/suggestion-slot';
import SuggestionSkeleton from '../traveler-profile-suggestions/suggestion-skeleton';
import DismissedSuggestionRow from '../traveler-profile-suggestions/dismissed-suggestion-row';
import SuggestionUndoButton from '../traveler-profile-suggestions/suggestion-undo-button';
import { useSignificantDatesSuggestionPreview } from '../../hooks/use-traveler-profile-suggestion-preview';
import formatSignificantDate from './utils';
import SignificantDatesView from './significant-dates-view';

const { OutlinedStarIcon } = OutlinedIcons;

type SignificantDateModalState =
  | null
  | { mode: 'create' }
  | { mode: 'edit'; entry: SignificantDate };

type TravelerProfileSignificantDatesProps = {
  travelerId: string | undefined;
  canEdit: boolean;
};

const SuggestedDatesList = ({ dates }: { dates: SignificantDate[] }) => (
  <Stack spacing={0}>
    {dates.map((entry, i) => (
      <Box
        // eslint-disable-next-line react/no-array-index-key
        key={`${entry.title}-${entry.date}-${i}`}
        sx={{ display: 'flex', alignItems: 'center', py: 0.25 }}
      >
        <Typography variant="body" sx={{ flex: 1 }}>
          {entry.title}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          {formatSignificantDate(entry.date)}
        </Typography>
      </Box>
    ))}
  </Stack>
);

const TravelerProfileSignificantDates = ({
  travelerId,
  canEdit,
}: TravelerProfileSignificantDatesProps) => {
  const { travelerProfile, updateTravelerProfile } = useTravelerProfile(travelerId);
  const [modalState, setModalState] = useState<SignificantDateModalState>(null);
  const preview = useSignificantDatesSuggestionPreview({
    hasContent: (travelerProfile?.significantDates?.length ?? 0) > 0,
  });
  // While an AI import review is in flight, lock add/edit/delete so the user
  // can't mutate this field outside the review flow.
  const lockedForReview = preview.state === 'idle' && preview.lockedForReview;

  const handleSave = async (entry: SignificantDate) => {
    const current = travelerProfile?.significantDates ?? [];
    const updated = modalState?.mode === 'edit'
      ? current.map((d) => (d.id === entry.id ? entry : d))
      : [...current, { title: entry.title, date: entry.date } as SignificantDate];
    await updateTravelerProfile(
      { significantDates: updated },
      'Error saving significant date.',
      'Significant dates saved.',
    );
    setModalState(null);
  };

  const handleDelete = async (id: string) => {
    const updated = (travelerProfile?.significantDates ?? []).filter((d) => d.id !== id);
    await updateTravelerProfile(
      { significantDates: updated },
      'Error deleting significant date.',
      'Significant date deleted.',
    );
  };

  const renderHeader = (showChip = false) => (
    <Stack direction="row" alignItems="center" spacing={2}>
      <OutlinedStarIcon fontSize="small" />
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="body">Significant Dates</Typography>
        {showChip && <AiGeneratedChip />}
      </Stack>
    </Stack>
  );

  if (preview.state === 'pending') {
    return (
      <Stack spacing={1}>
        {renderHeader(true)}
        <SuggestionSlot
          showChip={false}
          disabled={preview.disabled}
          onApply={preview.apply}
          onDismiss={preview.dismiss}
        >
          <SuggestedDatesList dates={preview.value} />
        </SuggestionSlot>
      </Stack>
    );
  }

  // Preview accepted suggestion in place. We render `SuggestedDatesList`
  // (not `SignificantDatesView`) because AI-suggested dates don't carry
  // server IDs yet, so keying rows by `entry.id` would produce duplicate
  // React keys. The list is intentionally read-only — staged-but-not-yet-
  // saved data shouldn't be mutated through the standard CRUD flows. An
  // Undo button lets the user roll back this single field without aborting
  // the whole review.
  if (preview.state === 'accepted') {
    return (
      <Stack spacing={1}>
        {renderHeader()}
        <SuggestedDatesList dates={preview.value} />
        <SuggestionUndoButton onClick={preview.undo} />
      </Stack>
    );
  }

  // Dismissed: keep the section header in place with a recoverable "skipped"
  // marker, mirroring the rich-text sections.
  if (preview.state === 'dismissed') {
    return (
      <Stack spacing={1}>
        {renderHeader()}
        <DismissedSuggestionRow onUndo={preview.undo} />
      </Stack>
    );
  }

  if (preview.state === 'loading') {
    return (
      <Stack spacing={1}>
        {renderHeader()}
        <SuggestionSkeleton lines={2} />
      </Stack>
    );
  }

  return (
    <>
      <SignificantDatesView
        dates={travelerProfile?.significantDates ?? []}
        onAdd={canEdit && !lockedForReview ? () => setModalState({ mode: 'create' }) : undefined}
        onEdit={canEdit && !lockedForReview ? (entry) => setModalState({ mode: 'edit', entry }) : undefined}
        onDelete={canEdit && !lockedForReview ? handleDelete : undefined}
      />

      <EditSignificantDateModal
        open={modalState !== null}
        onClose={() => setModalState(null)}
        onSave={handleSave}
        initialValues={modalState?.mode === 'edit' ? modalState.entry : undefined}
        isCreate={modalState?.mode === 'create'}
      />
    </>
  );
};

export default TravelerProfileSignificantDates;
